import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * useAttentionCounters — pollt systemweit die Anzahl der ROTEN Zustände
 * (Handlungsbedarf) für die Team-Sidebar (Feb 2026 · Sidebar-Zähler-Chips).
 *
 * Endpoint: `GET /api/team/attention-counters`
 * Return-Shape (server):
 *   { properties, requests, pending_bookings, unassigned_bookings, total }
 *
 * Poll-Intervall: 20 s — schnell genug, um binnen Sekunden auf ein
 * Team-Update zu reagieren, langsam genug, um die Server-Last minimal
 * zu halten. Bei Tab-Focus wird zusätzlich sofort neu geladen.
 *
 * Nutzung:
 *   const { counters } = useAttentionCounters(isTeam);
 *   counters.properties         // Objekte mit Handlungsbedarf
 *   counters.requests           // Anfragen mit Status „new" (Angefragt)
 *   counters.pending_bookings   // Buchungen mit Status „requested" (Angefragt)
 *   counters.unassigned_bookings  // Legacy — Buchungen ohne Concierge (Admin-only)
 */
export function useAttentionCounters(enabled, scopeConciergeId = null) {
  const [counters, setCounters] = useState({
    properties: 0,
    requests: 0,
    pending_bookings: 0,
    unassigned_bookings: 0,
    projects: 0,
    projects_blue: 0,
    unseen_properties: 0,
    unseen_requests: 0,
    unseen_bookings: 0,
    unseen_projects: 0,
    unseen_total: 0,
    total: 0,
  });
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      // Feb 2026 · 16 Sep 2026 · User-Directive „Admin-Impersonation zeigt
      // nur zugewiesene Daten": Wenn ein Admin einen Concierge impersoniert,
      // klammern wir die Sidebar-Zähler auf dessen Scope. Backend kennt den
      // Impersonations-Kontext nicht per se — der Client gibt ihn mit.
      const url = scopeConciergeId
        ? `/team/attention-counters?scope_concierge_id=${encodeURIComponent(scopeConciergeId)}`
        : "/team/attention-counters";
      const { data } = await api.get(url);
      if (!alive.current) return;
      setCounters({
        properties: Number(data?.properties || 0),
        requests: Number(data?.requests || 0),
        pending_bookings: Number(data?.pending_bookings || 0),
        unassigned_bookings: Number(data?.unassigned_bookings || 0),
        projects: Number(data?.projects || 0),
        projects_blue: Number(data?.projects_blue || 0),
        unseen_properties: Number(data?.unseen_properties || 0),
        unseen_requests: Number(data?.unseen_requests || 0),
        unseen_bookings: Number(data?.unseen_bookings || 0),
        unseen_projects: Number(data?.unseen_projects || 0),
        unseen_total: Number(data?.unseen_total || 0),
        total: Number(data?.total || 0),
      });
    } catch { /* silent — badge just won't update this cycle */ }
  }, [scopeConciergeId]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => { alive.current = false; };
    load();
    const iv = setInterval(load, 20000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, load]);

  return { counters, refresh: load };
}
